import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Typography } from "@mui/material";
import {
  AutoFixHighOutlined,
  DeleteOutlineRounded,
  SaveOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { getDocumentCraftingError } from "../../../Api/documentCrafting";
import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingRevisionDetail,
  DocumentCraftingRevisionSummary,
  DocumentCraftingRunStatus,
  DocumentCraftingSaveConflictError,
} from "../../../Models/documentCrafting";
import {
  useDiscardDocumentCraftingDraft,
  useDocumentCraftingWorkspaceSettings,
  useSaveDocumentCraftingDraft,
} from "../../../Query/useDocumentCrafting";
import ConfirmDialog from "../Common/ConfirmDialog";
import { ErrorState, LoadingState, Panel } from "../Common/DocumentCraftingUi";
import PagePreview, { PreviewUnit } from "../Common/PagePreview";
import { documentCraftingPaths } from "../Common/documentCraftingNavigation";
import { formatDateTime, useUserName } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";
import { isRunWorking } from "./detailModel";
import RefineDialog from "./RefineDialog";
import RevisionDownloads from "./RevisionDownloads";

interface Props {
  document: DocumentCraftingDocumentDetail;
  draft: DocumentCraftingRevisionSummary;
  detail: DocumentCraftingRevisionDetail | undefined;
  detailError: boolean;
  onRetryDetail: () => void;
  nativeExt: string;
  unit: PreviewUnit;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: color.gray500,
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * The caller's open draft: its run state, change requests, the model's diff
 * summary, the preview and downloads, and the three ways on: save as the
 * next revision (with the save-conflict question), refine, or discard.
 */
const DraftPanel: React.FC<Props> = ({
  document,
  draft,
  detail,
  detailError,
  onRetryDetail,
  nativeExt,
  unit,
}) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const userName = useUserName();
  const workspaceSettings = useDocumentCraftingWorkspaceSettings();
  const saveDraft = useSaveDocumentCraftingDraft();
  const discardDraft = useDiscardDocumentCraftingDraft();
  const [conflict, setConflict] =
    useState<DocumentCraftingSaveConflictError | null>(null);
  const [refineOpen, setRefineOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const nextNumber = document.revision_count + 1;
  const lastRun = detail?.last_run ?? null;
  const working = isRunWorking(lastRun);
  const awaiting = lastRun?.status === DocumentCraftingRunStatus.AWAITING_ANSWERS;
  const failed =
    lastRun?.status === DocumentCraftingRunStatus.FAILED ||
    lastRun?.status === DocumentCraftingRunStatus.CANCELLED;
  const hasResult = Boolean(detail?.artefacts.pdf);
  const canSave =
    document.permissions.can_edit && hasResult && !working && !awaiting;
  const busy = saveDraft.isPending || discardDraft.isPending;
  const ttlDays = workspaceSettings.data?.draft_ttl_days;

  const save = (force: boolean) =>
    saveDraft.mutate(
      { documentId: document.id, revisionId: draft.id, force },
      {
        onSuccess: (saved) => {
          setConflict(null);
          navigate(documentCraftingPaths.document(document.id, saved.number));
        },
        onError: (error) => {
          const body = getDocumentCraftingError(error);
          if (body?.code === "save_conflict") {
            setConflict(body as DocumentCraftingSaveConflictError);
          }
        },
      },
    );

  const discard = () =>
    discardDraft.mutate(
      { documentId: document.id, revisionId: draft.id },
      {
        onSuccess: () => {
          setDiscardOpen(false);
          navigate(documentCraftingPaths.document(document.id));
        },
      },
    );

  const conflictText = conflict
    ? [
        t("detail.draft.conflictText", {
          latest: conflict.latest_revision.number,
          user: userName(conflict.latest_revision.saved_by),
          date: formatDateTime(conflict.latest_revision.saved_at, i18n.language),
          next: conflict.next_number,
        }),
        conflict.base_revision_number !== null
          ? t("detail.draft.conflictBase", {
              base: conflict.base_revision_number,
            })
          : t("detail.draft.conflictNoBase"),
      ].join(" ")
    : "";

  const changeRequests = [
    draft.change_request?.trim(),
    ...draft.refinements.map((item) => item.change_request.trim()),
  ].filter((text): text is string => Boolean(text));

  return (
    <Panel
      title={t("detail.draft.title")}
      subtitle={
        draft.base_revision_number !== null
          ? t("detail.draft.subtitle", {
              number: draft.base_revision_number,
              date: formatDateTime(draft.created_at, i18n.language),
            })
          : t("detail.draft.subtitleNoBase", {
              date: formatDateTime(draft.created_at, i18n.language),
            })
      }
      sx={{ borderColor: color.orange200 }}
    >
      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        {ttlDays
          ? t("detail.draft.private", { count: ttlDays })
          : t("detail.draft.privateNoDays")}
      </Alert>

      {lastRun && (working || awaiting || failed) && (
        <Alert
          severity={failed ? "error" : awaiting ? "warning" : "info"}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(documentCraftingPaths.run(lastRun.id))}
            >
              {awaiting
                ? t("detail.notices.answerQuestions")
                : failed
                  ? t("detail.notices.openFailedRun")
                  : t("detail.notices.openRun")}
            </Button>
          }
        >
          {working
            ? t("detail.draft.working", {
                status: labels.runStatus(lastRun.status),
                progress: lastRun.progress,
              })
            : awaiting
              ? t("detail.draft.awaiting")
              : t("detail.draft.failed", {
                  status: labels.runStatus(lastRun.status),
                })}
        </Alert>
      )}

      {changeRequests.length > 0 && (
        <Section title={t("detail.draft.changeRequests")}>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {changeRequests.map((text, index) => (
              <Typography
                component="li"
                key={index}
                sx={{
                  fontSize: 13,
                  color: color.gray700,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {text}
              </Typography>
            ))}
          </Box>
        </Section>
      )}

      {draft.diff_summary && (
        <Section title={t("detail.draft.diffSummary")}>
          <Typography
            sx={{
              fontSize: 13,
              color: color.gray900,
              p: 1.25,
              borderRadius: "10px",
              backgroundColor: color.gray50,
              border: `1px solid ${color.gray200}`,
              whiteSpace: "pre-wrap",
            }}
          >
            {draft.diff_summary}
          </Typography>
        </Section>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveOutlined />}
          onClick={() => save(false)}
          disabled={!canSave || busy}
        >
          {t("detail.draft.save", { number: nextNumber })}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<AutoFixHighOutlined />}
          onClick={() => setRefineOpen(true)}
          disabled={!canSave || busy}
        >
          {t("detail.draft.refine")}
        </Button>
        <Button
          color="error"
          startIcon={<DeleteOutlineRounded />}
          onClick={() => setDiscardOpen(true)}
          disabled={busy}
        >
          {t("detail.draft.discard")}
        </Button>
      </Box>

      {detail ? (
        <>
          <Box sx={{ mb: 2 }}>
            <RevisionDownloads
              documentId={document.id}
              title={document.title}
              revisionId={draft.id}
              revisionNumber={null}
              artefacts={detail.artefacts}
              nativeExt={nativeExt}
              viewerTitle={`${document.title} · ${t("detail.draft.title")}`}
            />
          </Box>
          {detail.artefacts.pages > 0 ? (
            <PagePreview
              documentId={document.id}
              revisionId={draft.id}
              pageCount={detail.artefacts.pages}
              unit={unit}
            />
          ) : (
            <Typography sx={{ fontSize: 14, color: color.gray500 }}>
              {t("detail.draft.noPreview")}
            </Typography>
          )}
        </>
      ) : detailError ? (
        <ErrorState onRetry={onRetryDetail} />
      ) : (
        <LoadingState />
      )}

      <ConfirmDialog
        open={conflict !== null}
        title={t("detail.draft.conflictTitle")}
        text={conflictText}
        confirmLabel={t("detail.draft.conflictConfirm", {
          number: conflict?.next_number ?? nextNumber,
        })}
        pending={saveDraft.isPending}
        onConfirm={() => save(true)}
        onClose={() => setConflict(null)}
      />
      <ConfirmDialog
        open={discardOpen}
        title={t("detail.draft.discardTitle")}
        text={t("detail.draft.discardText")}
        confirmLabel={t("detail.draft.discard")}
        pending={discardDraft.isPending}
        danger
        onConfirm={discard}
        onClose={() => setDiscardOpen(false)}
      />
      <RefineDialog
        open={refineOpen}
        documentId={document.id}
        revisionId={draft.id}
        modelId={draft.model_id}
        onClose={() => setRefineOpen(false)}
        onStarted={(runId) => {
          setRefineOpen(false);
          navigate(documentCraftingPaths.run(runId));
        }}
      />
    </Panel>
  );
};

export default DraftPanel;
